import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

/**
 * HelpDrawerContext - Manages global state for the Help Center drawer
 *
 * This context provides:
 * - isOpen: whether the drawer is currently open
 * - open: function to open the drawer
 * - close: function to close the drawer
 * - toggle: function to toggle the drawer state
 */

interface HelpDrawerContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const HelpDrawerContext = createContext<HelpDrawerContextValue | undefined>(undefined);

interface HelpDrawerProviderProps {
  children: ReactNode;
}

export const HelpDrawerProvider: React.FC<HelpDrawerProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
    }),
    [isOpen, open, close, toggle]
  );

  return <HelpDrawerContext.Provider value={value}>{children}</HelpDrawerContext.Provider>;
};

export const useHelpDrawer = (): HelpDrawerContextValue => {
  const context = useContext(HelpDrawerContext);

  if (context === undefined) {
    throw new Error('useHelpDrawer must be used within a HelpDrawerProvider');
  }

  return context;
};

export default HelpDrawerContext;
